/**
 * LessonStatsService - Theo dõi thống kê học tập trong bài học
 */

export interface SectionStats {
  title?: string
  correct: number
  wrong: number
  hints: number
  ai: number
  textViewed: Set<string>   // khoá các khối text ĐÃ XEM (cuộn tới) trong phần — dùng tính tiến độ
}

export interface LessonStats {
  correctAnswers: number
  wrongAnswers: number
  hintsUsed: number
  aiQuestionsAsked: number
  totalQuestions: number
  startTime: number
  endTime?: number
  // Thống kê THEO TỪNG PHẦN (key = chỉ số phần) — LessonViewerPage đặt phần hiện tại
  // qua setCurrentSection; các record* tự cộng vào phần đang xem.
  sections: Record<number, SectionStats>
  videoWatched: number
  answeredKeys: Set<string>   // khoá các tương tác ĐÃ HOÀN THÀNH (vd '<cardId>:question-2') — để khôi phục khi load lại
}

class LessonStatsServiceClass {
  private stats: Map<string, LessonStats> = new Map()
  private currentLessonId: string | null = null
  private currentSection: number | null = null

  /** Đặt phần đang xem (LessonViewerPage gọi khi chuyển phần) để record* cộng đúng phần. */
  setCurrentSection(idx: number | null, title?: string, lessonId?: string): void {
    this.currentSection = idx
    if (idx === null) return
    const id = lessonId || this.currentLessonId
    if (!id) return
    const stats = this.stats.get(id)
    if (stats && !stats.sections[idx]) {
      stats.sections[idx] = { title, correct: 0, wrong: 0, hints: 0, ai: 0, textViewed: new Set() }
    } else if (stats && title && !stats.sections[idx].title) {
      stats.sections[idx].title = title
    }
  }

  /** Ghi nhận đã XEM một khối text (cuộn tới) trong phần đang xem — dedup theo key. */
  recordTextViewed(key: string, lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id || !key) return
    const sec = this.sectionBucket(id)
    if (sec) sec.textViewed.add(key)
  }

  /** Bucket thống kê của phần đang xem (tự tạo nếu chưa có). */
  private sectionBucket(id: string): SectionStats | null {
    if (this.currentSection === null) return null
    const stats = this.stats.get(id)
    if (!stats) return null
    if (!stats.sections[this.currentSection]) {
      stats.sections[this.currentSection] = { correct: 0, wrong: 0, hints: 0, ai: 0, textViewed: new Set() }
    }
    return stats.sections[this.currentSection]
  }

  /** Ghi nhận đã xem video (1 lần/phần — caller tự đảm bảo không trùng). */
  recordVideoWatched(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return
    const stats = this.stats.get(id)
    if (stats) {
      stats.videoWatched++
      console.log(`🎬 Video watched. Total: ${stats.videoWatched}`)
    }
  }

  /**
   * Bắt đầu theo dõi stats cho một bài học
   */
  // Bài đang xem (đặt bởi startLesson) — dùng làm fallback khi component không nhận prop lessonId.
  getCurrentLessonId(): string | null {
    return this.currentLessonId
  }

  startLesson(lessonId: string): void {
    this.currentLessonId = lessonId
    
    if (!this.stats.has(lessonId)) {
      this.stats.set(lessonId, {
        correctAnswers: 0,
        wrongAnswers: 0,
        hintsUsed: 0,
        aiQuestionsAsked: 0,
        totalQuestions: 0,
        startTime: Date.now(),
        sections: {},
        videoWatched: 0,
        answeredKeys: new Set()
      })
    }
    // Reset phần hiện tại — tránh dính chỉ số phần của bài xem trước đó (vd XemBaiHoc không dùng phần)
    this.currentSection = null

    console.log(`📊 LessonStatsService: Started tracking for lesson ${lessonId}`)
  }

  /** Ghi nhận 1 tương tác ĐÃ HOÀN THÀNH (theo khoá toàn cục) để khôi phục khi load lại. */
  recordAnswered(key: string, lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id || !key) return
    const stats = this.stats.get(id)
    if (stats) {
      if (!stats.answeredKeys) stats.answeredKeys = new Set()
      stats.answeredKeys.add(key)
    }
  }

  /** Lấy tập tương tác đã hoàn thành (bản sao) của 1 bài. */
  getAnsweredKeys(lessonId?: string): Set<string> {
    const id = lessonId || this.currentLessonId
    const stats = id ? this.stats.get(id) : null
    return stats?.answeredKeys ? new Set(stats.answeredKeys) : new Set()
  }

  /** Xuất stats 1 bài thành object thuần (Set → mảng) để lưu backend (meta_data.state.stats). */
  serialize(lessonId: string): any {
    const s = this.stats.get(lessonId)
    if (!s) return null
    const sections: any = {}
    for (const k of Object.keys(s.sections)) {
      const v = (s.sections as any)[k]
      sections[k] = { title: v.title, correct: v.correct, wrong: v.wrong, hints: v.hints, ai: v.ai, textViewed: Array.from(v.textViewed || []) }
    }
    return {
      correctAnswers: s.correctAnswers, wrongAnswers: s.wrongAnswers, hintsUsed: s.hintsUsed,
      aiQuestionsAsked: s.aiQuestionsAsked, totalQuestions: s.totalQuestions, videoWatched: s.videoWatched,
      sections, answeredKeys: Array.from(s.answeredKeys || []),
    }
  }

  /** Nạp lại stats 1 bài từ object đã lưu (mảng → Set). Dùng khi vào bài để khôi phục tiến độ. */
  hydrate(lessonId: string, data: any): void {
    if (!data || typeof data !== 'object') return
    const sections: Record<number, SectionStats> = {}
    const ds = data.sections || {}
    for (const k of Object.keys(ds)) {
      const v = ds[k] || {}
      sections[Number(k)] = {
        title: v.title, correct: v.correct || 0, wrong: v.wrong || 0, hints: v.hints || 0, ai: v.ai || 0,
        textViewed: new Set(Array.isArray(v.textViewed) ? v.textViewed : []),
      }
    }
    this.stats.set(lessonId, {
      correctAnswers: data.correctAnswers || 0, wrongAnswers: data.wrongAnswers || 0, hintsUsed: data.hintsUsed || 0,
      aiQuestionsAsked: data.aiQuestionsAsked || 0, totalQuestions: data.totalQuestions || 0,
      startTime: Date.now(), sections, videoWatched: data.videoWatched || 0,
      answeredKeys: new Set(Array.isArray(data.answeredKeys) ? data.answeredKeys : []),
    })
  }

  /**
   * Ghi nhận câu trả lời đúng
   */
  recordCorrectAnswer(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.correctAnswers++
      const sec = this.sectionBucket(id)
      if (sec) sec.correct++
      console.log(`✅ Correct answer recorded. Total: ${stats.correctAnswers}`)
    }
  }

  /**
   * Ghi nhận câu trả lời sai
   */
  recordWrongAnswer(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.wrongAnswers++
      const sec = this.sectionBucket(id)
      if (sec) sec.wrong++
      console.log(`❌ Wrong answer recorded. Total: ${stats.wrongAnswers}`)
    }
  }

  /**
   * Ghi nhận việc sử dụng gợi ý
   */
  recordHintUsed(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.hintsUsed++
      const sec = this.sectionBucket(id)
      if (sec) sec.hints++
      console.log(`💡 Hint used. Total: ${stats.hintsUsed}`)
    }
  }

  /**
   * Ghi nhận câu hỏi AI
   */
  recordAIQuestion(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.aiQuestionsAsked++
      const sec = this.sectionBucket(id)
      if (sec) sec.ai++
      console.log(`🤖 AI question asked. Total: ${stats.aiQuestionsAsked}`)
    }
  }

  /**
   * Đặt tổng số câu hỏi trong bài học
   */
  setTotalQuestions(total: number, lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    console.log(`🔍 DEBUG: setTotalQuestions called with total: ${total}, lessonId: ${lessonId}, resolved id: ${id}`)
    
    if (!id) {
      console.error(`❌ ERROR: No lessonId provided to setTotalQuestions`)
      return
    }

    const stats = this.stats.get(id)
    if (stats) {
      const oldTotal = stats.totalQuestions
      stats.totalQuestions = total
      console.log(`📝 Total questions set to: ${total} for lesson ${id} (was: ${oldTotal})`)
      
      // ✅ DEBUG: Kiểm tra logic đếm
      if (total <= 0) {
        console.warn(`⚠️ WARNING: Total questions is ${total} - this might be wrong!`)
      }
      if (total > 50) {
        console.warn(`⚠️ WARNING: Total questions is ${total} - seems too high!`)
      }
    } else {
      console.error(`❌ ERROR: No stats found for lessonId: ${id} when setting totalQuestions`)
      console.log(`🔍 Available lessonIds:`, Array.from(this.stats.keys()))
    }
  }

  /**
   * Kết thúc bài học
   */
  endLesson(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats && !stats.endTime) {
      stats.endTime = Date.now()
      console.log(`🏁 Lesson ended. Duration: ${this.getDuration(id)} seconds`)
    }
  }

  /**
   * Lấy stats của bài học hiện tại
   */
  getCurrentStats(): LessonStats | null {
    if (!this.currentLessonId) return null
    return this.stats.get(this.currentLessonId) || null
  }

  /**
   * Lấy stats của một bài học cụ thể
   */
  getStats(lessonId: string): LessonStats | null {
    return this.stats.get(lessonId) || null
  }

  /**
   * Tính thời gian học (giây)
   */
  getDuration(lessonId?: string): number {
    const id = lessonId || this.currentLessonId
    if (!id) return 0

    const stats = this.stats.get(id)
    if (!stats) return 0

    const endTime = stats.endTime || Date.now()
    return Math.floor((endTime - stats.startTime) / 1000)
  }

  /**
   * Tính tỷ lệ chính xác (%)
   */
  getAccuracy(lessonId?: string): number {
    const id = lessonId || this.currentLessonId
    if (!id) return 0

    const stats = this.stats.get(id)
    if (!stats) return 0

    const totalAttempts = stats.correctAnswers + stats.wrongAnswers
    if (totalAttempts === 0) return 0

    return Math.round((stats.correctAnswers / totalAttempts) * 100)
  }

  /**
   * Tính tiến độ (%)
   */
  getProgress(lessonId?: string): number {
    const id = lessonId || this.currentLessonId
    if (!id) return 0

    const stats = this.stats.get(id)
    if (!stats || stats.totalQuestions === 0) return 0

    // ✅ FIX: Đếm tổng câu đã trả lời (đúng + sai), không chỉ câu đúng
    const totalAnswered = stats.correctAnswers + stats.wrongAnswers
    const progress = Math.round((totalAnswered / stats.totalQuestions) * 100)
    
    // ✅ DEBUG: Log progress calculation
    console.log(`📊 Progress calculation: ${totalAnswered}/${stats.totalQuestions} = ${progress}%`)
    
    return progress
  }

  /**
   * Reset stats của bài học
   */
  resetStats(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    this.stats.delete(id)
    console.log(`🔄 Stats reset for lesson ${id}`)
  }

  /**
   * Xóa bài học hiện tại
   */
  clearCurrentLesson(): void {
    this.currentLessonId = null
    this.currentSection = null
  }

  /**
   * Kiểm tra xem có nên hiển thị stats không
   * (Dùng khi user chuyển sang phần khác)
   */
  shouldShowStats(lessonId?: string): boolean {
    const id = lessonId || this.currentLessonId
    if (!id) return false

    const stats = this.stats.get(id)
    if (!stats) return false

    // Hiển thị stats nếu user đã tương tác (trả lời ít nhất 1 câu)
    return stats.correctAnswers > 0 || stats.wrongAnswers > 0
  }

  /**
   * Lấy summary stats để hiển thị quick preview
   */
  getSummary(lessonId?: string): string {
    const id = lessonId || this.currentLessonId
    if (!id) return ''

    const stats = this.stats.get(id)
    if (!stats) return ''

    const accuracy = this.getAccuracy(id)
    const progress = this.getProgress(id)
    
    return `${stats.correctAnswers}/${stats.totalQuestions} câu đúng | ${accuracy}% | ${progress}% hoàn thành`
  }
}

// Export singleton instance
export const LessonStatsService = new LessonStatsServiceClass()

